
import React from "react";
import {
  ArrowUpRight,
  Car,
  Bike,
  Truck,
  Bus,
  Container,
  BadgeCheck,
  FileText,
  Check,
} from "lucide-react";

const Patenti = () => {
  return (
    <main className="bg-white">

      {/* HERO */}
      <section className="bg-[#123B6D] pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">

          <div className="max-w-4xl">

            <p className="text-lg font-bold uppercase tracking-[0.18em] text-[#D62828] mb-5">
              Le patenti
            </p>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] text-white">
              La patente giusta
              <br />
              per il tuo percorso.
            </h1>

            <p className="mt-7 max-w-2xl text-lg md:text-xl text-white/70 leading-relaxed">
              Dalla prima esperienza su due ruote alle patenti professionali.
              Ti aiutiamo a scegliere il percorso più adatto alle tue esigenze.
            </p>

          </div>

        </div>
      </section>


      {/* INTRO */}
      <section className="py-20 md:py-28">

        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">

            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#D62828] mb-4">
                Tutte le categorie
              </p>

              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#123B6D]">
                Scegli dove vuoi arrivare.
              </h2>
            </div>

            <p className="max-w-lg text-slate-500 text-base md:text-lg leading-relaxed">
              Ogni patente ha un percorso diverso. Qui trovi una panoramica
              delle principali categorie disponibili presso la nostra
              autoscuola.
            </p>

          </div>


          {/* PATENTI */}
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">


            {/* AM */}
            <a
              href="#patente-am"
              className="group relative overflow-hidden rounded-3xl bg-[#f5f7fa] border border-slate-200 p-7 md:p-8 min-h-[390px] flex flex-col hover:border-[#123B6D]/30 hover:shadow-xl transition-all duration-300"
            >

              <div className="flex items-start justify-between">

                <div className="w-14 h-14 rounded-2xl bg-[#123B6D] text-white flex items-center justify-center">
                  <Bike size={28} strokeWidth={1.7} />
                </div>

                <span className="text-5xl font-bold text-[#123B6D]/10">
                  AM
                </span>

              </div>

              <div className="mt-auto">

                <p className="text-sm font-bold uppercase tracking-widest text-[#D62828]">
                  Patente AM
                </p>

                <h3 className="mt-2 text-3xl font-semibold text-[#123B6D]">
                  Ciclomotori
                </h3>

                <p className="mt-3 text-slate-500 leading-relaxed">
                  Il primo passo verso l'indipendenza su due ruote.
                </p>

                <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-[#123B6D]">
                  Scopri la patente
                  <ArrowUpRight
                    size={18}
                    className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </div>

              </div>

            </a>


            {/* A */}
            <a
              href="#patente-a"
              className="group relative overflow-hidden rounded-3xl bg-[#123B6D] p-7 md:p-8 min-h-[390px] flex flex-col hover:shadow-xl transition-all duration-300"
            >

              <div className="flex items-start justify-between">

                <div className="w-14 h-14 rounded-2xl bg-white/10 text-white flex items-center justify-center">
                  <Bike size={28} strokeWidth={1.7} />
                </div>

                <span className="text-5xl font-bold text-white/10">
                  A
                </span>

              </div>

              <div className="mt-auto">

                <p className="text-sm font-bold uppercase tracking-widest text-[#D62828]">
                  Patenti A
                </p>

                <h3 className="mt-2 text-3xl font-semibold text-white">
                  Motocicli
                </h3>

                <p className="mt-3 text-white/65 leading-relaxed">
                  Per chi vuole vivere la strada su due ruote, con libertà e
                  sicurezza.
                </p>

                <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-white">
                  Scopri la patente
                  <ArrowUpRight
                    size={18}
                    className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </div>

              </div>

            </a>


            {/* B */}
            <a
              href="#patente-b"
              className="group relative overflow-hidden rounded-3xl bg-[#f5f7fa] border border-slate-200 p-7 md:p-8 min-h-[390px] flex flex-col hover:border-[#123B6D]/30 hover:shadow-xl transition-all duration-300"
            >

              <div className="flex items-start justify-between">

                <div className="w-14 h-14 rounded-2xl bg-[#D62828] text-white flex items-center justify-center">
                  <Car size={29} strokeWidth={1.7} />
                </div>

                <span className="text-5xl font-bold text-[#123B6D]/10">
                  B
                </span>

              </div>

              <div className="mt-auto">

                <p className="text-sm font-bold uppercase tracking-widest text-[#D62828]">
                  Patente B
                </p>

                <h3 className="mt-2 text-3xl font-semibold text-[#123B6D]">
                  Autoveicoli
                </h3>

                <p className="mt-3 text-slate-500 leading-relaxed">
                  La patente più richiesta per guidare la tua auto ogni giorno.
                </p>

                <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-[#123B6D]">
                  Scopri la patente
                  <ArrowUpRight
                    size={18}
                    className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </div>

              </div>

            </a>


            {/* C */}
            <a
              href="#patente-c"
              className="group relative overflow-hidden rounded-3xl bg-white border border-slate-200 p-7 md:p-8 min-h-[390px] flex flex-col hover:border-[#123B6D]/30 hover:shadow-xl transition-all duration-300"
            >

              <div className="flex items-start justify-between">

                <div className="w-14 h-14 rounded-2xl bg-[#123B6D] text-white flex items-center justify-center">
                  <Truck size={28} strokeWidth={1.7} />
                </div>

                <span className="text-5xl font-bold text-[#123B6D]/10">
                  C
                </span>

              </div>

              <div className="mt-auto">

                <p className="text-sm font-bold uppercase tracking-widest text-[#D62828]">
                  Patente C
                </p>

                <h3 className="mt-2 text-3xl font-semibold text-[#123B6D]">
                  Autocarri
                </h3>

                <p className="mt-3 text-slate-500 leading-relaxed">
                  Per guidare veicoli destinati al trasporto di merci.
                </p>

                <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-[#123B6D]">
                  Scopri la patente
                  <ArrowUpRight
                    size={18}
                    className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </div>

              </div>

            </a>


            {/* D */}
            <a
              href="#patente-d"
              className="group relative overflow-hidden rounded-3xl bg-[#f5f7fa] border border-slate-200 p-7 md:p-8 min-h-[390px] flex flex-col hover:border-[#123B6D]/30 hover:shadow-xl transition-all duration-300"
            >

              <div className="flex items-start justify-between">

                <div className="w-14 h-14 rounded-2xl bg-[#123B6D] text-white flex items-center justify-center">
                  <Bus size={28} strokeWidth={1.7} />
                </div>

                <span className="text-5xl font-bold text-[#123B6D]/10">
                  D
                </span>

              </div>

              <div className="mt-auto">

                <p className="text-sm font-bold uppercase tracking-widest text-[#D62828]">
                  Patente D
                </p>

                <h3 className="mt-2 text-3xl font-semibold text-[#123B6D]">
                  Autobus
                </h3>

                <p className="mt-3 text-slate-500 leading-relaxed">
                  Per la guida di autobus e veicoli destinati al trasporto
                  passeggeri.
                </p>

                <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-[#123B6D]">
                  Scopri la patente
                  <ArrowUpRight
                    size={18}
                    className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </div>

              </div>

            </a>


            {/* E */}
            <a
              href="#patente-e"
              className="group relative overflow-hidden rounded-3xl bg-[#123B6D] p-7 md:p-8 min-h-[390px] flex flex-col hover:shadow-xl transition-all duration-300"
            >

              <div className="flex items-start justify-between">

                <div className="w-14 h-14 rounded-2xl bg-white/10 text-white flex items-center justify-center">
                  <Container size={28} strokeWidth={1.7} />
                </div>

                <span className="text-5xl font-bold text-white/10">
                  E
                </span>

              </div>

              <div className="mt-auto">

                <p className="text-sm font-bold uppercase tracking-widest text-[#D62828]">
                  Patente E
                </p>

                <h3 className="mt-2 text-3xl font-semibold text-white">
                  Rimorchi
                </h3>

                <p className="mt-3 text-white/65 leading-relaxed">
                  Per completare le categorie necessarie alla guida di
                  complessi di veicoli.
                </p>

                <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-white">
                  Scopri la patente
                  <ArrowUpRight
                    size={18}
                    className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </div>

              </div>

            </a>

          </div>

        </div>

      </section>


      {/* PATENTI PROFESSIONALI */}
      <section className="bg-[#f5f7fa] py-24 md:py-32">

        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">

          <div className="max-w-3xl">

            <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#D62828] mb-4">
              Abilitazioni professionali
            </p>

            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#123B6D]">
              Se vuoi trasformare
              <br />
              la guida in un lavoro.
            </h2>

            <p className="mt-6 text-lg text-slate-500 leading-relaxed">
              Percorsi e abilitazioni per chi vuole lavorare nel settore
              dell'autotrasporto e del trasporto professionale.
            </p>

          </div>


          <div className="grid md:grid-cols-2 gap-5 mt-14">


            {/* CAP */}
            <a
              href="#cap"
              className="group bg-white rounded-3xl border border-slate-200 p-8 md:p-10 hover:border-[#123B6D]/30 hover:shadow-xl transition-all duration-300"
            >

              <div className="flex items-start justify-between">

                <div className="w-14 h-14 rounded-2xl bg-[#D62828] text-white flex items-center justify-center">
                  <BadgeCheck size={27} strokeWidth={1.7} />
                </div>

                <ArrowUpRight
                  size={24}
                  className="text-[#123B6D] transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                />

              </div>

              <h3 className="mt-10 text-3xl font-semibold text-[#123B6D]">
                CAP
              </h3>

              <p className="mt-3 text-slate-500 leading-relaxed max-w-md">
                Certificato di abilitazione professionale per specifiche
                attività di guida.
              </p>

            </a>


            {/* CQC */}
            <a
              href="#cqc"
              className="group bg-[#123B6D] rounded-3xl p-8 md:p-10 hover:shadow-xl transition-all duration-300"
            >

              <div className="flex items-start justify-between">

                <div className="w-14 h-14 rounded-2xl bg-white/10 text-white flex items-center justify-center">
                  <FileText size={27} strokeWidth={1.7} />
                </div>

                <ArrowUpRight
                  size={24}
                  className="text-white transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                />

              </div>

              <h3 className="mt-10 text-3xl font-semibold text-white">
                CQC
              </h3>

              <p className="mt-3 text-white/65 leading-relaxed max-w-md">
                La qualificazione necessaria per determinate attività
                professionali legate al trasporto.
              </p>

            </a>

          </div>

        </div>

      </section>


      {/* CTA */}
      <section className="py-24 md:py-32">

        <div className="max-w-5xl mx-auto px-5 text-center">

          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#123B6D]/5 text-[#123B6D] flex items-center justify-center">
            <Check size={30} strokeWidth={2} />
          </div>

          <h2 className="mt-7 text-4xl md:text-5xl font-semibold tracking-tight text-[#123B6D]">
            Non sai quale patente ti serve?
          </h2>

          <p className="mt-5 max-w-2xl mx-auto text-lg text-slate-500 leading-relaxed">
            Ti aiutiamo a capire quale categoria è più adatta alle tue
            esigenze e quali sono i passaggi necessari per conseguirla.
          </p>

          <a
            href="#contatti"
            className="
              inline-flex
              items-center
              gap-3
              mt-8
              px-7
              py-4
              rounded-xl
              bg-[#D62828]
              hover:bg-[#b91f1f]
              text-white
              font-semibold
              transition-all
            "
          >
            Parla con noi
            <ArrowUpRight size={19} />
          </a>

        </div>

      </section>

    </main>
  );
};

export default Patenti;
